import { Button, Typography } from "@hellocarbo/lasagne";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import background from "@/assets/fr/sign-up/carousel/background.png";
import collaborate1 from "@/assets/fr/sign-up/carousel/collaborate-1.png";
import collaborate2 from "@/assets/fr/sign-up/carousel/collaborate-2.png";
import dashboard from "@/assets/fr/sign-up/carousel/dashboard.png";
import trajectory from "@/assets/fr/sign-up/carousel/trajectory.png";
import { cn } from "@/utils/classes";

const Carousel = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [previousStep, setPreviousStep] = useState<number>(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [animationOrigin, setAnimationOrigin] = useState<"left" | "right">(
    "right"
  );
  const [isPaused, setIsPaused] = useState(false);

  const { t } = useTranslation(["common", "signUp"]);

  const goToNextStep = () => {
    setIsTransitioning(true);
    setAnimationOrigin("right");
    setPreviousStep(currentStep);
    setCurrentStep((currentStep + 1) % 3);
  };

  const goToPreviousStep = () => {
    setIsTransitioning(true);
    setAnimationOrigin("left");
    setPreviousStep(currentStep);
    setCurrentStep((currentStep - 1 + 3) % 3);
  };

  // Change the current step after 8 second (except when the mouse is hovering the carousel)
  useEffect(() => {
    if (isPaused) {
      return;
    }

    const id = setTimeout(() => {
      goToNextStep();
    }, 8000);

    return () => {
      clearTimeout(id);
    };
  }, [currentStep, isPaused]);

  const getAnimationClassName = (step: number) => {
    // Needed to not animate out on page load
    if (!isTransitioning) {
      return;
    }

    const classNameArray = ["duration-[2s]"];

    // We animate in the current step
    if (step === currentStep) {
      classNameArray.push("animate-in");

      // Depending on the arrow clicked we slide in from the right or the left
      if (animationOrigin === "left") {
        classNameArray.push("slide-in-from-left-[150%]");
      } else {
        classNameArray.push("slide-in-from-right-[150%]");
      }
    } else {
      // We animate out the previous step
      classNameArray.push("animate-out z-[-1]");
      classNameArray.push("fade-out");

      if (animationOrigin === "left") {
        classNameArray.push("slide-out-to-right-[100%] ");
      } else {
        classNameArray.push("slide-out-to-left-[150%]");
      }
    }

    return classNameArray;
  };

  // We need to keep the previous step in the dom until the animation is done
  const handleAnimationEnd = () => {
    setIsTransitioning(false);
  };

  return (
    <div
      className="relative hidden h-full w-full overflow-hidden rounded-lg sm:block"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="pb-lg gap-lg relative z-10 flex h-full w-full flex-col items-center justify-end">
        <div className="pt-2xl pl-2xl absolute left-0 h-full w-screen">
          {(currentStep === 0 || (previousStep === 0 && isTransitioning)) && (
            <>
              <img
                src={collaborate2}
                className={cn(
                  "absolute top-[15%] left-[10%] h-[50%] object-cover object-left",
                  getAnimationClassName(0)
                )}
                role="presentation"
                onAnimationEnd={handleAnimationEnd}
              />
              <img
                src={collaborate1}
                className={cn(
                  "absolute bottom-[15%] h-[40%] object-cover object-left",
                  getAnimationClassName(0)
                )}
                role="presentation"
              />
            </>
          )}
          {(currentStep === 1 || (previousStep === 1 && isTransitioning)) && (
            <img
              src={dashboard}
              className={cn(
                "absolute h-[80%] object-cover object-left",
                getAnimationClassName(1)
              )}
              role="presentation"
              onAnimationEnd={handleAnimationEnd}
            />
          )}
          {(currentStep === 2 || (previousStep === 2 && isTransitioning)) && (
            <img
              src={trajectory}
              className={cn(
                "absolute h-[80%] object-cover object-left",
                getAnimationClassName(2)
              )}
              role="presentation"
              onAnimationEnd={handleAnimationEnd}
            />
          )}
        </div>
        <div
          className="px-xl gap-md z-10 flex w-full items-center justify-between"
          aria-live="polite"
        >
          {currentStep === 0 && (
            <Typography
              step="0"
              as="span"
              weight="semibold"
              className="text-white"
            >
              {t("signUp:carousel.step1")}
            </Typography>
          )}
          {currentStep === 1 && (
            <Typography
              step="0"
              as="span"
              weight="semibold"
              className="text-white"
            >
              {t("signUp:carousel.step2")}
            </Typography>
          )}
          {currentStep === 2 && (
            <Typography
              step="0"
              as="span"
              weight="semibold"
              className="text-white"
            >
              {t("signUp:carousel.step3")}
            </Typography>
          )}
          <div className="gap-sm flex flex-row">
            <Button
              size="small"
              Icon={ArrowLeft}
              intent="primary"
              shape="soft"
              onClick={goToPreviousStep}
            >
              {t("previous")}
            </Button>
            <Button
              size="small"
              Icon={ArrowRight}
              intent="primary"
              shape="soft"
              onClick={goToNextStep}
            >
              {t("next")}
            </Button>
          </div>
        </div>
      </div>
      <img
        src={background}
        className="absolute top-[-5px] left-[-5px] h-full w-full scale-[1.04] object-cover blur-[5px]"
      />
    </div>
  );
};

export default Carousel;
